import { useCallback, useEffect, useMemo, useState } from 'react';
import { fetchDashboardSnapshots, fetchQualityAffectedLeads, generateDashboardSnapshot, initializeQualityControl } from '../api';
import type { DashboardSnapshot, DashboardSnapshotType } from '../types';
import { usePolling } from '../hooks/usePolling';

type Breakdown = Record<string, unknown>[];

function parseJson(value: string | null): unknown {
  if (!value) return null;
  try { return JSON.parse(value); } catch { return null; }
}

function formatDate(value: string | null) {
  return value ? new Date(value).toLocaleString() : '—';
}

function breakdownRows(value: string | null): Breakdown {
  const parsed = parseJson(value);
  return Array.isArray(parsed) ? parsed as Breakdown : [];
}

function severityClass(value: string) {
  if (value === 'ready') return 'bg-emerald-100 text-emerald-700';
  if (value === 'watch') return 'bg-amber-100 text-amber-700';
  if (value === 'repair') return 'bg-orange-100 text-orange-700';
  return 'bg-red-100 text-red-700';
}

function QualityMetricGrid({ value }: { value: string | null }) {
  const parsed = parseJson(value);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [affected, setAffected] = useState<{ lead_id: string; phone_identity: string; source: string; status: string }[]>([]);
  if (!parsed || typeof parsed !== 'object') return null;
  const openAffected = async (name: string) => {
    if (expanded === name) { setExpanded(null); return; }
    setExpanded(name); const result = await fetchQualityAffectedLeads(name); setAffected(result.leads);
  };
  return (
    <div className="mt-2">
      <div className="space-y-2">{Object.entries(parsed as Record<string, Record<string, unknown>>).map(([name, metric]) => <div key={name} className="rounded-xl border border-slate-100 bg-slate-50 p-2.5"><div className="flex items-center justify-between gap-2"><span className="min-w-0 truncate text-xs font-medium capitalize text-slate-700">{name.replaceAll('_', ' ')}</span><span className={`flex-none rounded-full px-1.5 py-0.5 text-[10px] font-semibold uppercase ${severityClass(String(metric.severity ?? 'blocked'))}`}>{String(metric.severity ?? '—')}</span></div><div className="mt-1 text-sm font-semibold text-slate-900">{String(metric.current_value ?? 0)}% <span className="text-[10px] font-normal text-slate-400">/ {String(metric.target_value ?? 0)}%</span></div><p className="mt-1 text-[11px] leading-4 text-slate-500">{String(metric.explanation ?? '—')}</p><button type="button" onClick={() => void openAffected(name)} className="mt-1 text-left text-[11px] font-medium leading-4 text-slate-700 underline decoration-slate-300 underline-offset-2">{String(metric.recommended_action ?? '—')} · {String(metric.affected_count ?? 0)} affected</button>{expanded === name && <div className="mt-2 border-t border-slate-200 pt-2">{affected.length === 0 ? <p className="text-[11px] text-slate-400">No affected leads.</p> : affected.map((lead) => <div key={lead.lead_id} className="flex justify-between gap-2 py-1 text-[11px]"><span className="truncate text-slate-600">{lead.phone_identity || lead.lead_id}</span><span className="flex-none text-slate-400">{lead.status}</span></div>)}</div>}</div>)}</div>
    </div>
  );
}

function SnapshotCard({
  type,
  snapshot,
  onGenerate,
  canGenerate,
}: {
  type: DashboardSnapshotType;
  snapshot: DashboardSnapshot | undefined;
  onGenerate: (type: DashboardSnapshotType) => void;
  canGenerate: boolean;
}) {
  const summary = useMemo(() => {
    const parsed = parseJson(snapshot?.summary_json ?? null);
    return parsed && typeof parsed === 'object' ? parsed as Record<string, unknown> : {};
  }, [snapshot?.summary_json]);
  const [showQualityControl, setShowQualityControl] = useState(false);
  const isBusy = snapshot?.status === 'generating';
  const isReady = snapshot?.status === 'ready';
  const sourceRows = breakdownRows(snapshot?.source_breakdown_json ?? null);
  const statusRows = breakdownRows(snapshot?.status_breakdown_json ?? null);
  const agentRows = breakdownRows(snapshot?.agent_breakdown_json ?? null);
  const title = type === 'morning' ? 'Morning snapshot' : 'EOD snapshot';
  const actionLabel = !canGenerate
    ? 'View saved snapshot'
    : isReady
    ? 'Already generated'
    : isBusy
      ? 'Generating…'
      : snapshot?.status === 'failed'
        ? 'Retry snapshot'
        : `Generate ${type === 'morning' ? 'Morning' : 'EOD'} snapshot`;

  return (
    <section className="min-w-0 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      <div className="border-b border-slate-100 bg-gradient-to-br from-slate-50 to-white p-4">
        <div className="flex items-start gap-3">
          <div className="flex h-9 w-9 flex-none items-center justify-center rounded-xl bg-emerald-100 text-sm font-bold text-emerald-700">
            {type === 'morning' ? 'AM' : 'PM'}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center justify-between gap-2">
              <p className="truncate text-sm font-semibold text-slate-900">{title}</p>
              <span className={`rounded-full px-2 py-1 text-[10px] font-semibold uppercase tracking-wide ${isReady ? 'bg-emerald-100 text-emerald-700' : snapshot?.status === 'failed' ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-600'}`}>
                {snapshot?.status ?? 'not generated'}
              </span>
            </div>
            <p className="mt-1 text-xs text-slate-500">
              {snapshot ? formatDate(snapshot.generated_at) : 'No snapshot saved for today'}
            </p>
            {snapshot?.generated_by && <p className="mt-0.5 truncate text-xs text-slate-400">Generated by {snapshot.generated_by}</p>}
          </div>
        </div>
        <button
          type="button"
          onClick={() => onGenerate(type)}
          disabled={!canGenerate || isBusy || isReady}
          className="mt-3 w-full rounded-xl bg-wechat-accent px-3 py-2.5 text-xs font-semibold text-white shadow-sm transition hover:brightness-95 disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-500 disabled:shadow-none"
        >
          {actionLabel}
        </button>
      </div>

      {snapshot?.status === 'failed' && (
        <p className="mt-3 rounded-lg bg-red-50 p-2 text-xs text-red-700">{snapshot.error_message || 'Generation failed'}</p>
      )}
      {isReady && (
        <div className="p-4">
          <div className="grid grid-cols-2 gap-2">
            {[
              ['Unique leads', 'unique_leads'], ['Assigned', 'assigned_leads'],
              ['Qualified', 'qualified_leads'], ['Consultations booked', 'consultations_booked'],
              ['Attended', 'consultations_attended'], ['Treatment started', 'treatment_started'],
            ].map(([label, key]) => <div key={key} className="min-w-0 rounded-xl border border-slate-100 bg-slate-50 p-2.5"><div className="truncate text-[11px] leading-4 text-slate-500">{label}</div><div className="mt-0.5 text-lg font-semibold leading-6 text-slate-900">{String(summary[key] ?? (key === 'unique_leads' ? summary.total_leads : 0))}</div></div>)}
          </div>
          <div className="mt-4 border-t border-slate-100 pt-3">
            <button type="button" onClick={() => setShowQualityControl((value) => !value)} className="flex w-full items-center justify-between text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">
              <span>Quality control</span>
              <span className="text-slate-400">{showQualityControl ? 'Hide' : 'View metrics'}</span>
            </button>
            {showQualityControl && <QualityMetricGrid value={snapshot?.quality_metrics_json ?? null} />}
          </div>
          <BreakdownTable title="Source breakdown" rows={sourceRows} nameKey="source" countKey="count" />
          <BreakdownTable title="Status breakdown" rows={statusRows} nameKey="status" countKey="count" />
          <BreakdownTable title="Agent breakdown" rows={agentRows} nameKey="agent" countKey="total_leads" />
        </div>
      )}
    </section>
  );
}

function BreakdownTable({ title, rows, nameKey, countKey }: { title: string; rows: Breakdown; nameKey: string; countKey: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-4 border-t border-slate-100 pt-3">
      <button type="button" onClick={() => setOpen((value) => !value)} className="flex w-full items-center justify-between text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500"><span>{title}</span><span className="text-slate-400">{open ? 'Hide' : 'View'}</span></button>
      {open && (rows.length === 0 ? <p className="mt-2 text-xs text-slate-400">—</p> : <div className="mt-2 grid max-h-36 grid-cols-2 gap-x-4 gap-y-1.5 overflow-y-auto">{rows.map((row, index) => <div key={`${String(row[nameKey])}-${index}`} className="flex min-w-0 items-center justify-between gap-2 text-xs"><span className="min-w-0 truncate text-slate-600">{String(row[nameKey] ?? '—')}</span><span className="flex-none font-semibold tabular-nums text-slate-900">{String(row[countKey] ?? 0)}</span></div>)}</div>)}
    </div>
  );
}

export default function AdminDashboardSnapshots({ active }: { active: boolean }) {
  const [snapshots, setSnapshots] = useState<DashboardSnapshot[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [selectedType, setSelectedType] = useState<DashboardSnapshotType>('morning');
  const [initializing, setInitializing] = useState(false);

  const load = useCallback(async () => {
    try { setSnapshots((await fetchDashboardSnapshots(selectedDate)).snapshots); setError(null); }
    catch (e) { setError(e instanceof Error ? e.message : 'Snapshot read failed'); }
  }, [selectedDate]);

  useEffect(() => { if (active) void load(); }, [active, load]);
  const snapshotsGenerating = snapshots.some((snapshot) => snapshot.status === 'generating');
  usePolling(load, 2_000, [active, load, snapshotsGenerating], {
    enabled: active && snapshotsGenerating,
    pauseWhenHidden: true,
  });

  const byType = (type: DashboardSnapshotType) => snapshots.find((snapshot) => snapshot.snapshot_type === type);
  const today = new Date().toISOString().slice(0, 10);
  const generate = async (type: DashboardSnapshotType) => {
    try { setError(null); const result = await generateDashboardSnapshot(type); setSnapshots((current) => [...current.filter((item) => item.snapshot_type !== type), result.snapshot]); }
    catch (e) { setError(e instanceof Error ? e.message : 'Snapshot generation failed'); await load(); }
  };
  const initialize = async () => {
    try { setInitializing(true); setError(null); await initializeQualityControl(); await load(); }
    catch (e) { setError(e instanceof Error ? e.message : 'QC initialization failed'); }
    finally { setInitializing(false); }
  };
  const needsInitialization = snapshots.length === 0 || snapshots.some((snapshot) => snapshot.status === 'ready' && !snapshot.quality_metrics_json);

  return (
    <div className="space-y-3">
      <div className="border-b border-slate-200 pb-3">
        <div className="flex items-start justify-between gap-3"><div><h2 className="text-lg font-semibold tracking-tight text-slate-900">Quality Control Tower</h2><p className="mt-1 text-xs leading-5 text-slate-500">Saved Morning and EOD quality snapshots.</p></div><input type="date" value={selectedDate} onChange={(event) => setSelectedDate(event.target.value)} className="rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-xs text-slate-700" aria-label="Snapshot date" /></div>
        {needsInitialization && selectedDate === today && <button type="button" onClick={() => void initialize()} disabled={initializing} className="mt-3 w-full rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-800 disabled:opacity-50">{initializing ? 'Initializing QC data…' : 'Initialize QC data'}</button>}
        <div className="mt-3 flex gap-1.5">{(['morning', 'eod'] as const).map((type) => <button key={type} type="button" onClick={() => setSelectedType(type)} className={`rounded-full px-3 py-1.5 text-[11px] font-semibold capitalize ${selectedType === type ? 'bg-slate-900 text-white' : 'border border-slate-200 bg-white text-slate-600'}`}>{type}</button>)}</div>
      </div>
      {error && <p className="rounded-lg bg-red-50 p-3 text-xs text-red-700">{error}</p>}
      <div className="grid grid-cols-1 gap-3">
        {selectedType === 'morning' && <SnapshotCard type="morning" snapshot={byType('morning')} onGenerate={generate} canGenerate={selectedDate === today} />}
        {selectedType === 'eod' && <SnapshotCard type="eod" snapshot={byType('eod')} onGenerate={generate} canGenerate={selectedDate === today} />}
      </div>
    </div>
  );
}
